import { useEffect, useState } from "react";
import { ExternalLink, Tag, Store } from "lucide-react";
import api from "@/lib/api";

// Cuelinks-powered affiliate results, shown BELOW Suffle's own listings (never above).
// module = "shop" | "travel". Silent no-op when Cuelinks is dormant / returns nothing.
export const AffiliateDeals = ({ module = "shop", query = "", category = "", limit = 12, hideMerchants = [] }) => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    const qp = new URLSearchParams({ module, limit: String(limit) });
    if (query) qp.set("q", query);
    if (category) qp.set("category", category);
    api.get(`/marketplace/public/affiliate-deals?${qp.toString()}`)
      .then((r) => { if (live) setDeals(r.data?.enabled ? (r.data.deals || []) : []); })
      .catch(() => { if (live) setDeals([]); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [module, query, category, limit]);

  const shown = (hideMerchants && hideMerchants.length)
    ? deals.filter((d) => !hideMerchants.includes((d.merchant || "").toLowerCase()))
    : deals;
  if (!loading && shown.length === 0) return null;

  return (
    <div className="mt-8" data-testid={`affiliate-deals-${module}`}>
      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-base font-semibold text-slate-700">More deals from our partners</h3>
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">Affiliate</span>
      </div>
      {loading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-52 animate-pulse rounded-2xl bg-white/70" data-testid="affiliate-skeleton" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {shown.map((d) => <DealCard key={d.id} d={d} module={module} />)}
        </div>
      )}
    </div>
  );
};

// Route every partner click through the branded hand-off screen (/out) which mints a fresh
// trackable deep link (+ Sub-ID) and shortens it before redirecting to the partner.
const outHref = (u, src = "cuelinks", m = "") => `/out?u=${encodeURIComponent(u || "")}&src=${src}${m ? `&m=${encodeURIComponent(m)}` : ""}`;

const DealCard = ({ d }) => {
  const [logoOk, setLogoOk] = useState(true);
  const [imgOk, setImgOk] = useState(true);
  const initial = (d.merchant || d.domain || "?").trim().charAt(0).toUpperCase() || "?";
  return (
    <a
      href={outHref(d.url, "cuelinks", d.merchant)} target="_blank" rel="nofollow sponsored noopener noreferrer"
      data-testid={`affiliate-deal-${d.id}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition hover:-translate-y-0.5 hover:shadow-lg"
    >
      {/* Clean, on-brand hero: the merchant banner/logo shown CONTAINED on white so the whole logo
          is visible (never cropped), else the favicon logo, else the merchant initial. */}
      <div className="relative flex h-32 items-center justify-center overflow-hidden border-b border-slate-100 bg-white p-4">
        {d.image && imgOk ? (
          <img src={d.image} alt={d.merchant || "deal"} className="max-h-full max-w-full object-contain" loading="lazy" onError={() => setImgOk(false)} />
        ) : (d.retailer_logo && logoOk) ? (
          <img src={d.retailer_logo} alt={d.merchant || "retailer"} className="max-h-16 max-w-[70%] object-contain" onError={() => setLogoOk(false)} loading="lazy" />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-50 text-2xl font-bold text-indigo-500 ring-1 ring-slate-200" data-testid={`affiliate-initial-${d.id}`}>
            {initial}
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col p-3">
        {d.merchant && <p className="mb-0.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400"><Store className="h-3 w-3" />{d.merchant}</p>}
        <p className="line-clamp-2 text-sm font-medium text-slate-800">{d.title}</p>
        <div className="mt-auto flex flex-wrap items-center justify-between gap-1.5 pt-2">
          {d.price != null ? (
            <span className="font-bold text-slate-900">₹{d.price}</span>
          ) : d.payout_pct != null ? (
            <span className="truncate font-bold text-emerald-600" data-testid={`affiliate-payout-${d.id}`}>Upto {d.payout_pct}% payout</span>
          ) : d.percent_off ? (
            <span className="font-bold text-emerald-600">{d.percent_off}% off</span>
          ) : (
            <span className="text-xs font-semibold text-emerald-600 capitalize">{d.offer_type || "Deal"}</span>
          )}
          {d.coupon_code && (
            <span className="inline-flex items-center gap-1 rounded-md border border-dashed border-amber-400 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-700" data-testid={`affiliate-coupon-${d.id}`}>
              <Tag className="h-3 w-3" />{d.coupon_code}
            </span>
          )}
        </div>
        <span className="mt-2 inline-flex items-center justify-center gap-1 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white transition group-hover:bg-slate-700">
          Grab deal <ExternalLink className="h-3 w-3" />
        </span>
      </div>
    </a>
  );
};

export default AffiliateDeals;
